/**
 * VertexQuickCard - Tarjeta para acceso a Vertex IA (Beta)
 * 
 * Muestra herramientas IA experimentales en el Dashboard
 * Solo visible si el usuario tiene agentes VRT asignados
 * (Builder está disponible en sidebar como "VRT Lab Builder" solo para admin)
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MessageSquare, BarChart3, Users, Lock, ChevronDown, CheckCircle2, Loader2, Send } from 'lucide-react';
import { VERTEX_API_BASE } from '../utils/vertexApiHelper';
import { getSafeToken } from '../utils/tokenHelper';

const SIMULATOR_OPTIONS = [
  'Ventas B2B / Negociación comercial',
  'Presentación a inversores / StartUp',
  'Habilidades blandas ejecutivas',
];

const LS_KEY = 'vrt_access_pending';
const EXPIRE_MS = 7 * 24 * 60 * 60 * 1000; // 7 días

function readPending() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Migrar formato antiguo (objeto único → array)
    if (data?.requestedAt && !Array.isArray(data?.requests)) {
      return { requests: [{ simulatorType: data.simulatorType, requestedAt: data.requestedAt }], lastRequestedAt: data.requestedAt };
    }
    if (!Array.isArray(data?.requests) || data.requests.length === 0) return null;
    return data;
  } catch { return null; }
}

function daysAgo(ts) {
  return Math.floor((Date.now() - ts) / (1000 * 60 * 60 * 24));
}

const RequestAccessPanel = ({ user }) => {
  const [simulatorType, setSimulatorType] = useState('');
  const [message, setMessage] = useState('');

  // idle | sending | pending | expired | error
  const [status, setStatus] = useState(() => {
    const p = readPending();
    if (!p) return 'idle';
    if (Date.now() - p.lastRequestedAt > EXPIRE_MS) return 'expired';
    return 'pending';
  });
  const [pendingData, setPendingData] = useState(() => readPending());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!simulatorType) return;
    setStatus('sending');

    try {
      const token = getSafeToken();
      const res = await fetch(`${VERTEX_API_BASE}/v4/request-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ simulatorType, message }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Error');

      // Actualizar array deduplicado — misma categoría no crea línea nueva
      const prev = readPending();
      const existingRequests = prev?.requests || [];
      const alreadyExists = existingRequests.some(r => r.simulatorType === simulatorType);
      const updatedRequests = alreadyExists
        ? existingRequests
        : [...existingRequests, { simulatorType, requestedAt: Date.now() }];
      const newEntry = { requests: updatedRequests, lastRequestedAt: Date.now() };
      try { localStorage.setItem(LS_KEY, JSON.stringify(newEntry)); } catch {}
      setPendingData(newEntry);
      setStatus('pending');
    } catch {
      setStatus('error');
    }
  };

  const handleRequestAnother = () => {
    // NO borrar localStorage — conservar solicitudes previas para acumularlas
    setSimulatorType('');
    setMessage('');
    setStatus('idle');
  };

  if (status === 'pending') {
    const days = pendingData ? daysAgo(pendingData.lastRequestedAt) : 0;
    const timeLabel = days === 0 ? 'hoy' : days === 1 ? 'hace 1 día' : `hace ${days} días`;
    const requests = pendingData?.requests || [];
    return (
      <div className="mt-10 mb-8 max-w-md">
        <div className="flex flex-col gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 px-5 py-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-[14px] font-semibold text-slate-800">Solicitud{requests.length > 1 ? 'es' : ''} en espera</p>
              <p className="text-xs text-slate-500 leading-snug mt-0.5">
                Última solicitud {timeLabel} · el equipo habilitará tu acceso en breve.
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            {requests.map((r, i) => (
              <div key={i} className="flex items-center gap-2 bg-white border border-slate-100 rounded-xl px-4 py-2.5">
                <Lock className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                <span className="text-sm text-slate-600 truncate">{r.simulatorType}</span>
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={handleRequestAnother}
          className="mt-4 text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2 transition-colors"
        >
          Solicitar un simulador diferente
        </button>
      </div>
    );
  }

  if (status === 'expired') {
    const days = pendingData ? daysAgo(pendingData.lastRequestedAt) : 7;
    const requests = pendingData?.requests || [];
    return (
      <div className="mt-10 mb-8 max-w-md">
        <div className="flex flex-col gap-3 rounded-2xl border border-amber-100 bg-amber-50/60 px-5 py-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Lock className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <p className="text-[14px] font-semibold text-slate-800">Acceso pendiente</p>
              <p className="text-xs text-slate-500 leading-snug mt-0.5">
                Solicitaste acceso hace {days} días. Puedes reenviar el recordatorio.
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            {requests.map((r, i) => (
              <div key={i} className="flex items-center gap-2 bg-white border border-slate-100 rounded-xl px-4 py-2.5">
                <Lock className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                <span className="text-sm text-slate-600 truncate">{r.simulatorType}</span>
              </div>
            ))}
          </div>
          <button
            onClick={handleRequestAnother}
            className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-700 text-white text-sm font-medium rounded-xl transition-colors duration-150 w-fit"
          >
            <Send className="w-3.5 h-3.5" /> Reenviar solicitud
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-10 mb-8 max-w-md">
      {/* Encabezado ligero */}
      <div className="flex items-center gap-2 mb-1">
        <Lock className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-xs text-slate-400 font-medium tracking-wide">VRT–Coach · Acceso por solicitud</span>
      </div>
      <h2 className="text-xl font-semibold text-slate-800 mb-1">Solicita tu simulador</h2>
      <p className="text-sm text-slate-400 mb-6 leading-relaxed">
        Elige el área que quieres entrenar y el equipo te habilitará el acceso.
      </p>

      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Select — excluye opciones ya solicitadas */}
        {(() => {
          const alreadyRequested = new Set((pendingData?.requests || []).map(r => r.simulatorType));
          const availableOptions = SIMULATOR_OPTIONS.filter(opt => !alreadyRequested.has(opt));
          if (availableOptions.length === 0) {
            return (
              <p className="text-sm text-slate-400 py-2">Ya solicitaste acceso a todas las áreas disponibles.</p>
            );
          }
          return (
            <div className="relative">
              <select
                value={simulatorType}
                onChange={e => setSimulatorType(e.target.value)}
                required
                className="w-full appearance-none bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 pr-9 transition-shadow"
              >
                <option value="">¿Qué área quieres entrenar?</option>
                {availableOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          );
        })()}

        {/* Textarea solo si seleccionó algo */}
        {simulatorType && (
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="Contexto adicional (opcional)…"
            className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 resize-none transition-shadow"
          />
        )}

        {status === 'error' && (
          <p className="text-xs text-red-500 pl-1">No se pudo enviar. Intenta de nuevo.</p>
        )}

        <button
          type="submit"
          disabled={!simulatorType || status === 'sending'}
          className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded-xl transition-colors duration-150"
        >
          {status === 'sending'
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enviando…</>
            : <><Send className="w-3.5 h-3.5" /> Solicitar acceso</>}
        </button>

        <p className="text-[11px] text-slate-300 leading-relaxed pt-1">
          El acceso se habilita manualmente por el administrador.
        </p>
      </form>
    </div>
  );
};

const VertexQuickCard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const assignedAgents = user?.assignedAgents || [];

  // Sin agentes asignados → panel de solicitud de acceso
  if (assignedAgents.length === 0) {
    return <RequestAccessPanel user={user} />;
  }

  // Herramientas disponibles - Diseño orientado a valor educativo
  const tools = [
    {
      id: 'simulator',
      title: 'Simulaciones con IA',
      description: 'Practica conversaciones y escenarios guiados por inteligencia artificial para mejorar tu criterio y habilidades.',
      icon: MessageSquare,
      route: '/vertex/lab',
      gradient: 'from-slate-700 to-slate-800',
      available: true,
      badge: 'Disponible'
    }
  ];

  const handleToolClick = (route, available) => {
    if (available && route !== '#') {
      navigate(route);
    }
  };

  return (
    <div className="space-y-4 mt-8 mb-8">
      {/* Header con valor claro */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-gray-900">
            VRT–Coach
          </h2>
          <span className="px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-bold rounded-full shadow-lg">
            BETA
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
          <span className="text-sm text-gray-600">{tools.filter(t => t.available).length} de {tools.length} activos</span>
        </div>
      </div>

      <p className="text-sm text-gray-600">
        Entrenamiento conversacional potenciado por inteligencia artificial
      </p>

      {/* Grid alineado con EPL (3 columnas) para mantener proporción */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
        {tools.map((tool) => {
          const Icon = tool.icon;
          
          return (
            <div
              key={tool.id}
              onClick={() => handleToolClick(tool.route, tool.available)}
              className={`bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 border border-gray-200 group flex flex-col ${
                tool.available ? 'hover:shadow-xl hover:-translate-y-1 hover:border-indigo-300 cursor-pointer' : 'opacity-90'
              }`}
            >
              {/* Header con gradiente - Estilo EPL */}
              <div className={`bg-gradient-to-r ${tool.gradient} p-6 text-white relative overflow-hidden`}>
                {/* Patrón de fondo sutil */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 right-0 w-32 h-32">
                    <Icon className="w-full h-full" />
                  </div>
                </div>
                
                {/* Contenido del header */}
                <div className="relative z-10 flex items-start justify-between">
                  <div className="p-2 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm">
                    <Icon className="w-6 h-6" />
                  </div>
                  <span className="px-3 py-1 bg-white bg-opacity-25 rounded-full text-xs font-medium backdrop-blur-sm">
                    {tool.badge}
                  </span>
                </div>
                
                <h3 className="text-xl font-bold mt-4 relative z-10">
                  {tool.title}
                </h3>
              </div>

              {/* Body con descripción y CTA */}
              <div className="bg-white p-6 flex flex-col flex-1">
                <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-grow">
                  {tool.description}
                </p>
                
                {/* Footer minimalista */}
                <div className="pt-4 border-t border-gray-100 flex items-center justify-end">
                  <div className={`flex items-center gap-2 text-sm font-bold transition-colors duration-300 ${
                    tool.available ? 'text-indigo-600 group-hover:text-indigo-700' : 'text-gray-400'
                  }`}>
                    <span className="relative">
                      {tool.available ? (tool.id === 'simulator' ? 'Ingresar' : 'Ver análisis') : 'Próximamente'}
                      {tool.available && (
                        <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-indigo-600 transition-all duration-300 group-hover:w-full"></span>
                      )}
                    </span>
                    {tool.available && (
                      <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VertexQuickCard;
